/*
 * Copyright (c) 2023 SmartWifi - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential. No warranty, explicit or implicit provided. In
 * no event will the author be liable for any claim or damages.
 */

import { useContext, useEffect } from "react";
import { ContextUpdater, LoginContext } from "../../Context";
import { useNavigate } from "react-router-dom";
import { Button, Input, Label } from "reactstrap";
import { requestLogin, requestLoginVerify } from "../../requests/login/Requests";
import { useUpdateState } from "../../util/State";
import GoogleLoginButton from "./social/GoogleLoginButton";
import debug from "../../util/Debug";

import smartWifiLogo from "../../assets/images/sw_logo.svg";
import smartWifiIcon from "../../assets/images/sw_icon.svg";
import FacebookLoginButton from "./social/FacebookLoginButton";
import TwitterLoginButton from "./social/TwitterLoginButton";
import { customDomainLogo, getCustomDomain, isCustomDomain } from "../../util/CustomDomain";
import { requestDataConfig } from "../../requests/data/Requests";

const Login = () => {
  // context
  const contextUpdater = useContext(ContextUpdater);
  const loginContext = useContext(LoginContext);

  // state
  const initialState = { type: null, email: "", sms: "", verify: "", loading: true };
  const [state, updateState] = useUpdateState(initialState);

  // navigator
  const navigate = useNavigate();

  const hasEmail = loginContext?.config?.hasEmailConfig ?? true;
  const hasSms = loginContext?.config?.hasSmsConfig ?? true;
  const hasFacebook = loginContext?.config?.hasFacebookConfig ?? true;
  const hasGoogle = loginContext?.config?.hasGoogleConfig ?? true;
  const hasTwitter = loginContext?.config?.hasTwitterConfig ?? true;

  // constants
  const loginOptions = [
    { type: "email", text: "Email", icon: "envelope", element: Button, enabled: hasEmail },
    { type: "sms", text: "SMS", icon: "telephone", element: Button, enabled: hasSms },
    { type: "google", text: "Google", icon: "google", element: GoogleLoginButton, enabled: hasGoogle },
    { type: "facebook", text: "Facebook", icon: "facebook", element: FacebookLoginButton, enabled: hasFacebook },
    { type: "twitter", text: "Twitter/X", icon: "twitter", element: TwitterLoginButton, enabled: hasTwitter },
    // { type: "apple", text: "Apple", icon: "apple", element: AppleLoginButton },
    // { type: "linedkin", text: "LinkedIn", icon: "linkedin", element: LinkedInLoginButton },
  ];
  
  // Load config if custom domain
  useEffect(() => {
    if (isCustomDomain()) {
      requestDataConfig({ 
        context: contextUpdater,
        customDomain: getCustomDomain(),
        onSuccess: () => {
          updateState({ loading: false });
        },
        onFailure: () => {
          return "Configuration error";
        },
      })
    } else {
      updateState({ loading: false })
    }
  }, [])

  // handlers
  const handleBack = () => {
    if (loginContext.verifyPending) {
      loginContext.updateState({ verifyPending: false });
    } else {
      updateState({ type: null });
    }
  };

  const handleNext = () => {
    if (loginContext.verifyPending) {
      handleLoginVerify();
    } else {
      handleLogin();
    }
  }

  const handleLogin = () => {
    const requestProps = { type: state.type };
    let errorMessage = null;
    switch (state.type) {
      case "email":
        requestProps.email = state.email;
        errorMessage = "Invalid email address";
        break;
      case "sms":
        requestProps.sms = state.sms.replaceAll(" ", "").replaceAll("-", "");
        errorMessage = "Invalid phone number";
        break;
      default:
        debug.error("Invalid request type: +" + state.type);
        return;
    }

    requestLogin({
      ...requestProps,
      context: contextUpdater,
      onFailure: (message, code) => {
        if (code === 400 && errorMessage != null) {
          return errorMessage;
        }
        return message;
      }
    });
  };

  const handleLoginVerify = () => {
    const requestProps = { type: state.type, code: state.verify };
    switch (state.type) {
      case "email": requestProps.email = state.email; break;
      case "sms": requestProps.sms = state.sms; break;
      default:
        debug.error("Invalid request type: +" + state.type);
        return;
    }

    requestLoginVerify({
      ...requestProps,
      context: contextUpdater,
      onSuccess: () => navigate("/dashboard"),
      onFailure: (message, code) => {
        switch (code) {
          case 403: return "Invalid verification code";
          default: return message;
        }
      },
    });
  };

  // renderers
  const loginInput = () => {
    const { type, email, sms, verify } = state;
    const { verifyPending } = loginContext;

    // styles
    const containerStyle = {
      marginTop: "12px",
      width: "400px",
    };

    // bottom controls
    const loginControls = () => (
      <div className="pt-2 d-flex justify-content-between">
        <Button color="dark" onClick={handleBack}>Back</Button>
        <Button color="green" onClick={handleNext}>{verifyPending ? "Verify" : "Login"}</Button>
      </div>
    );

    // return verification input
    if (verifyPending) {
      // set text based on type
      let title, description;
      switch (type) {
        case "email":
          title = <h4>Verify Email</h4>;
          description = <span>A verification code has been sent to <strong>{email}</strong></span>;
          break;
        case "sms":
          title = <h4>Verify Phone Number</h4>;
          description = <span>A verification code has been sent to <strong>{sms}</strong></span>;
          break;
        default:
          debug.warn("Invalid login verify type: " + type);
          return null;
      }

      return (
        <div align="center" style={containerStyle}>
          <Label className="no-spacing">{title}</Label>
          <Label className="no-spacing">{description}</Label>
          <Input
            className="pt-2"
            type="text"
            placeholder="Verification Code"
            value={verify}
            onChange={e => updateState({ verify: e.target.value })}
          />
          {loginControls()}
        </div>
      );
    }

    // return based on type
    switch (type) {
      case "email": return (
        <div style={containerStyle}>
          <Label className="no-spacing">Enter Email</Label>
          <Input
            type="email"
            placeholder="sample@email.com"
            value={email}
            onChange={e => updateState({ email: e.target.value })}
          />
          {loginControls()}
        </div>
      );
      case "sms": return (
        <div style={containerStyle}>
          <Label className="no-spacing">Enter Phone Number</Label>
          <Input
            type="tel"
            placeholder="1 222-333-4444"
            value={sms}
            onChange={e => updateState({ sms: e.target.value })}
          />
          {loginControls()}
        </div>
      );
      case undefined: case null: return (
        <div style={containerStyle}>
          {loginOptions.filter((i) => i.enabled ).map(({type, text, icon, element: Element}, index) => (
            <Element
              key={index}
              className="mb-2 w-100"
              color="blue"
              onClick={() => updateState({ type })}
            >
              <i className={`pe-2 bi-${icon}`} />
              <span>{text}</span>
            </Element>
          ))}
        </div>
      );
      default:
        debug.warn("Invalid login type: " + type);
        return null;
    }
  }

  return (
    <div className="fill-container flex-dynamic d-flex flex-column align-items-center">
      {isCustomDomain() ? (
          <div className="ps-3 pt-5 pb-1 flex-static d-flex justify-content-center">
            <img src={customDomainLogo()} alt="" style={{ height: "50px" }} />
          </div>
        ) : (
          <div className="ps-3 pt-5 pb-1 flex-static d-flex justify-content-center">
            <img src={smartWifiLogo} alt="" style={{ height: "36px" }} />
            <img src={smartWifiIcon} alt="" style={{ height: "36px" }} />
          </div>
        )
      }
      <Label className="pb-2 w-100 text-center" tag="h3">Guest Data Dashboard</Label>
      <Label className="pt-4 pb-2 w-100 text-center fw-bold" tag="h1">Select Login Method</Label>
      {!state.loading && loginInput()}
    </div>
  );
};

export default Login;
